import _ from "lodash";
import React, { useEffect, useState,useRef } from "react";

import IconButton from "components/elements/IconButton";
import Table from "components/elements/Table";
import Select from "components/elements/Select";
import dataProvider from "core/DataProvider";
import AsyncSpotSelect from "components/elements/AsyncSelect";
import Modal from "components/elements/Modal";

import { toast } from "react-hot-toast";
import { ReactComponent as EyeIcon } from "../../../images/eye.svg";
import { ReactComponent as AddIcon } from "../../../images/add.svg";
import { ReactComponent as EditIcon } from "../../../images/edit.svg";
import IconInput from "components/elements/IconInput";
import utility from "core/Utils";

const headerMap = {
  group_id: "Group ID",
  group_name: "Group Name",
  advertiser_count: "Advertisers",
  action: "Actions",
};

const addHeaders = {
  label: "Advertiser",
  action: "Actions",
};

const editHeaders = {
  label: "Advertiser",
  subscription_count: "Subscription Count",
  action: "Actions",
};

const GroupAdvertisers = () => {
  // View States
  const [showForm, setShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // Data States
  const [list, setList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [advertisers, setAdvertisers] = useState([]); // for form listing
  const [groupAdvertisers, setGroupAdvertisers] = useState([]); // for viewing modal

  // selections
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchedAdvertiser, setSearchedAdvertiser] = useState({});
  const [selectedAdvertiser, setSelectedAdvertisers] = useState(null);
const isSubmitting = useRef(false);
  // loads all existing group advertiser
  const getList = async () => {
    setList([]);
    try {
      let d = await dataProvider.network.get("group-advertisers");
      utility.sortBy(d, "group_name");
      setList(d);
    } catch (error) {
      toast.error("Failed to load group advertiser");
    } finally {
      setLoading(false);
    }
  };

  // loads list of groups for the select input
  const getGroups = async () => {
    try {
      let d = await dataProvider.network.get("min-master/groups?isGA=true");
      if (d) {
        for (let i = 0; i < d.length; i++) {
          d[i] = { value: d[i].id, label: d[i].name };
        }
        setGroups(d);
      }
    } catch (error) {
      toast.error("Failed to load groups");
    }
  };

  // async select functions
  const advertiserLoadOptions = React.useCallback(
    _.debounce((inputText, callback) => {
      if (inputText.length > 2) {
        advertiserAutoSelect(inputText).then(options => callback(options));
      }
    }, 300),
    []
  );

  // async select functions
  const advertiserAutoSelect = async val => {
    const d = await dataProvider.network.get(
      "min-master/advertisers?ac=" + val
    );
    return d.map(x => {
      return { value: x.id, label: x.name };
    });
  };

  // submits form
  const createGroupAdvertiser = async () => {
    if (isSubmitting.current) return;  // 🚫 instant block
  isSubmitting.current = true;
    let body = {
      group_id: selectedGroup.value,
      advertiser_id: advertisers.map(x => x.value),
    };

    try {
       setLoading(true);
      await dataProvider.network.post("group-advertisers", body);
      toast.success("Group advertisers updated");

      getList();
      setIsEdit(false);
      setAdvertisers([]);
      setShowForm(false);
      setSelectedGroup(null);

    } catch (error) {
      toast.error("Failed to add group advertiser");
    } finally {
    setLoading(false);
    isSubmitting.current = false;   // 🔓 unlock
  }
  };

  // loads advertiser list of given group for editing
  const loadAdvertisers = async () => {
    try {
      let advs = await dataProvider.network.get(
        "min-master/group-advertisers?group-id=" + selectedGroup.value
      );
      setAdvertisers(
        advs.map(x => ({
          label: x.name,
          value: x.id,
          subscription_count: x.subscription_count,
        }))
      );
      if (advs.length) {
        setIsEdit(true);
      }
    } catch (error) {
      toast.error("Failed to load advertisers");
    }
  };

  // loads advertiser list of given group
  const loadGroupAdvertisers = async groupID => {
    try {
      let advs = await dataProvider.network.get(
        "min-master/group-advertisers?group-id=" + groupID
      );
      setGroupAdvertisers(advs);
    } catch (error) {
      toast.error("Failed to load advertisers");
    }
  };

  // adds the advertiser to the list of group's advertisers
  const addAdvertiser = e => {
    let exists = false;
    let existingList = [...advertisers];

    for (let i = 0; i < existingList.length; i++) {
      if (existingList[i].value === e.value) {
        exists = true;
        break;
      }
    }

    if (!exists) {
      setAdvertisers([...existingList, e]);
    }
  };

  // remove advertiser from list in form based on value
  const removeAdvertiser = val => {
    setAdvertisers(advertisers.filter(x => x.value !== val));
  };

  // user clicked edit, set the selected group rest will happen
  const editGroup = obj => {
    setShowForm(true);
    setSelectedGroup({ value: obj.group_id, label: obj.group_name });
  };

  // load group list and group advertiser listing to show
  useEffect(() => {
    getList();
    getGroups();
  }, []);

  // add action button to group advertisers listing
  useEffect(() => {
    if (list.length) {
      setList(
        list.map(x => ({
          ...x,
          action: (
            <div className="action flex flex-v-center">
              <div
                className="action-button flex flex-center"
                onClick={() => {
                  loadGroupAdvertisers(x.group_id);
                }}
              >
                <EyeIcon />
              </div>
              <div
                className="action-button flex flex-center"
                onClick={() => {
                  editGroup(x);
                }}
              >
                <EditIcon />
              </div>
            </div>
          ),
        }))
      );
    }
  }, [list.length]);

  // add action button to advertisers list in the form
  useEffect(() => {
    if (advertisers.length) {
      let updatedAdvertisers = [];
      for (let i = 0; i < advertisers.length; i++) {
        let obj = { ...advertisers[i] };

        if (obj.subscription_count > 0) {
          obj.action = <div className="action flex flex-v-center"></div>;
        } else {
          obj.action = (
            <div className="action flex flex-v-center">
              <div
                className="action-button flex flex-center"
                onClick={() => {
                  removeAdvertiser(obj.value);
                }}
              >
                <AddIcon style={{ transform: "rotate(45deg)" }} />
              </div>
            </div>
          );
        }
        updatedAdvertisers.push(obj);
      }

      setAdvertisers(updatedAdvertisers);
    }
  }, [advertisers.length]);

  // load list of group's advertisers when selected group is changed
  useEffect(() => {
    if (selectedGroup) {
      loadAdvertisers();
    }
  }, [selectedGroup]);

  let modal = (
    <Modal
      onClose={() => {
        setGroupAdvertisers([]);
      }}
    >
      <div className="flex">
        <div className="row">
          <h2>Advertisers</h2>
        </div>
      </div>
      <div className="flex mtop" style={{ minWidth: "30vw" }}>
        <div
          className="row sub-preview-lists"
          style={{ paddingRight: "0", marginLeft: "0" }}
        >
          <div className="item-lists">
            {groupAdvertisers.map((adv, idx) => (
              <div className="row full-row" key={idx}>
                {adv.name}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );

  return (
    <section id="client-list-container">
      {groupAdvertisers.length > 0 ? modal : ""}
      <div className="flex">
        <div className="right flex flex-v-center">
          <IconButton
            icon="add"
            text={showForm ? (isEdit ? "Update" : "Save") : "Add"}
            onClick={_ => {
              if (showForm) {
                createGroupAdvertiser();
              } else {
                setShowForm(!showForm);
              }
            }}
            disabled={loading}
          />
          {showForm && (
            <IconButton text="Cancel" onClick={_ => setShowForm(false)} />
          )}
        </div>
      </div>
      {showForm && (
        <div className="box mtop pad-1">
          <h3 className="pad-lr">Add Group Advertiser</h3>
          <div className="mtop">
            <div className="prow flex flex-v-center">
              <div className="el flex-full">
                <Select
                  placeholder="Group"
                  value={selectedGroup}
                  options={groups}
                  onChange={e => {
                    setSelectedGroup(e);
                  }}
                />
              </div>
              <div className="el flex-full">
                <AsyncSpotSelect
                  placeholder="Advertisers"
                  value={selectedAdvertiser}
                  loadOptions={advertiserLoadOptions}
                  onChange={e => {
                    addAdvertiser(e);
                  }}
                  onInputChange={e => setSearchedAdvertiser(e)}
                  noOptionsMessage={() => "Type to search (min 3 characters)"}
                />
              </div>
            </div>
            <div className="prow" style={{ margin: "1rem", padding: "1rem 0" }}>
              <Table
                headers={isEdit ? editHeaders : addHeaders}
                data={advertisers}
              />
            </div>
          </div>
        </div>
      )}
      <div className="box mtop pad-1">
        <div className="flex flex-v-center">
          <h3 className="pad-lr">Group Advertisers</h3>
          <div className="right flex">
            <div className="el">
              {loading && (
                <div className="loader">
                  <div className="lds-hourglass"></div>
                </div>
              )}
            </div>
            <div className="el pad-lr">
              <IconInput
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search (min 3 character)"
                icon="search"
                style={{ minWidth: "250px" }}
              />
            </div>
          </div>
        </div>
        <div className="mtop">
          <Table headers={headerMap} data={list} search={search} />
        </div>
      </div>
    </section>
  );
};

export default GroupAdvertisers;
